import { RouteComponentProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { PageLayout, getPageStatus } from 'components/PageLayout';
import { Flooding } from 'features/flooding/flooding.types';
import { useAuthState } from 'state/useAuthState';
import {
  useFloodingGeoEvent,
  floodingDataPatchConfigs,
  floodingFieldPatchConfigs,
} from 'features/flooding/hooks/useFloodingGeoEvent';
import { ReportForm } from 'features/reports/components/ReportForm';

type ContentProps = {
  geoEvent: Flooding;
};

const Content = (props: ContentProps): JSX.Element => {
  const { geoEvent } = props;
  const { t } = useTranslation();

  const getViewPath = (geoEventId: number): string => {
    return `/i/${geoEventId}`;
  };

  return (
    <>
      <Helmet>
        <title>
          {t('addIncidentReport.titleWithName', { name: geoEvent.name })}
        </title>
      </Helmet>
      <ReportForm
        geoEvent={geoEvent}
        geoEventPatchConfigs={{
          dataPatchConfigs: floodingDataPatchConfigs,
          fieldPatchConfigs: floodingFieldPatchConfigs,
        }}
        getViewPath={getViewPath}
      />
    </>
  );
};

export const AddFloodingReport = (
  props: RouteComponentProps<{ geoEventId: string }>,
): JSX.Element => {
  const {
    match: {
      params: { geoEventId },
    },
  } = props;
  const { t } = useTranslation();
  const {
    permissions: { canReportFlooding },
  } = useAuthState();

  const { geoEvent, isGeoEventLoading, isGeoEventFetchError } =
    useFloodingGeoEvent({
      id: Number(geoEventId),
    });

  const status = getPageStatus({
    isUnauthorized:
      !canReportFlooding || (geoEvent && geoEvent.geoEventType !== 'flooding'),
    isLoading: isGeoEventLoading,
    isError: isGeoEventFetchError || !geoEvent,
  });

  return (
    <PageLayout
      data-cname="AddFloodingReportPage"
      status={status}
      headerBarProps={{
        title: geoEvent
          ? t('addIncidentReport.titleWithName', { name: geoEvent.name })
          : undefined,
        action: 'back',
      }}
    >
      {geoEvent && <Content geoEvent={geoEvent} />}
    </PageLayout>
  );
};
